import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { cs, enUS } from "date-fns/locale"

import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  formatIsoDateDmy,
  isoDateToLocalDate,
  localDateToIsoDate,
  parseDmyToIsoDate,
} from "@/lib/dateFormat"

const TEXTS = {
  cs: {
    invalid: "Zadejte datum ve formátu DD/MM/RRRR",
    openCalendar: "Vybrat datum v kalendáři",
    previousMonth: "Předchozí měsíc",
    nextMonth: "Další měsíc",
  },
  en: {
    invalid: "Enter the date as DD/MM/YYYY",
    openCalendar: "Pick a date from the calendar",
    previousMonth: "Previous month",
    nextMonth: "Next month",
  },
}

const toText = (value) => (value ? formatIsoDateDmy(value) : "")

/**
 * Date field shown as DD/MM/YYYY in both app languages, with a calendar popover (week starts on Monday).
 * The native <input type="date"> cannot do this: its display format follows the browser/OS locale.
 * `value`/`onChange` carry the wire format "YYYY-MM-DD"; incomplete or invalid text reports "" so the
 * caller's required-field check applies. Typing also accepts "." as the separator (no numeric inputMode:
 * the iOS digit keypad has no "/"); the text is normalised to DD/MM/YYYY on blur.
 */
const DateInput = React.forwardRef(
  ({ value, onChange, language = "cs", className, disabled, "data-testid": testId, ...props }, ref) => {
    const texts = TEXTS[language] || TEXTS.en
    const inputRef = React.useRef(null)
    React.useImperativeHandle(ref, () => inputRef.current)
    const [text, setText] = React.useState(() => toText(value))
    const [open, setOpen] = React.useState(false)

    // Resync only when the value changed from outside (form reset, opening an edit) — never while the
    // typed text still stands for the same value, otherwise a half-typed date would be wiped.
    React.useEffect(() => {
      if ((parseDmyToIsoDate(text) || "") !== (value || "")) setText(toText(value))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const invalid = text.trim() !== "" && !parseDmyToIsoDate(text)
    React.useEffect(() => {
      inputRef.current?.setCustomValidity(invalid ? texts.invalid : "")
    }, [invalid, texts.invalid])

    const handleTextChange = (e) => {
      setText(e.target.value)
      onChange?.(parseDmyToIsoDate(e.target.value) || "")
    }

    const handleBlur = () => {
      const iso = parseDmyToIsoDate(text)
      if (iso) setText(formatIsoDateDmy(iso))
    }

    const handleSelect = (date) => {
      // react-day-picker reports a click on the already selected day as `undefined` — keep the value, just close.
      if (!date) {
        setOpen(false)
        return
      }
      const iso = localDateToIsoDate(date)
      setText(formatIsoDateDmy(iso))
      onChange?.(iso)
      setOpen(false)
    }

    const selected = isoDateToLocalDate(value)

    return (
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          autoComplete="off"
          placeholder="DD/MM/YYYY"
          maxLength={10}
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          data-testid={testId}
          className={cn("pr-9", className)}
          {...props}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={texts.openCalendar}
              title={texts.openCalendar}
              data-testid={testId ? `${testId}-calendar-button` : undefined}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-500 hover:text-slate-900 disabled:pointer-events-none disabled:opacity-50"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          {/* Above the dialog, whose content sits at zIndex 9999 (ui/dialog.jsx). */}
          <PopoverContent className="w-auto p-0" align="end" style={{ zIndex: 10000 }}>
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              onSelect={handleSelect}
              locale={language === "cs" ? cs : enUS}
              weekStartsOn={1}
              labels={{ labelPrevious: () => texts.previousMonth, labelNext: () => texts.nextMonth }}
            />
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)
DateInput.displayName = "DateInput"

export { DateInput }
